import React, { useCallback, useMemo, useState } from "react";
import {
  Card,
  CardBody,
  Container,
  Row,
  Col,
  Label,
  Form,
  Button,
  ModalFooter,
  ModalBody,
  ModalHeader,
  Modal,
} from "reactstrap";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import TableContainer from "../../components/Common/TableContainer";
import { post } from "../../helpers/api_helper";
import { LEDGER_REPORT, SEND_PDF } from "../../helpers/url_helper";
import { useForm, Controller } from "react-hook-form";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/material_blue.css";
import toastr from "toastr";
import { PDFDownloadLink, PDFViewer } from "@react-pdf/renderer";
import ledgerColumns from "components/Columns/LedgerReport";
import LedgerReportPdf from "./LedgerReportPdf";

const REPORT_TYPE = {
  LEDGER_REPORT: "ledger",
};

const LedgerReport = () => {
  const [report, setReport] = useState([]);
  const [pageIndex, setPageIndex] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(false);
  const [isReportGenerated, setIsReportGenerated] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showPdfModal, setShowPdfModal] = useState(false);
  const [fromDate, setFromDate] = useState(new Date());
  const [toDate, setToDate] = useState(new Date());
  const breadcrumbItems = [
    { title: "valfuel", link: "/" },
    { title: "Reports", link: "#" },
  ];

  const { handleSubmit, control } = useForm({
    defaultValues: {
      customer_ul_id: [],
      start_date: new Date().toISOString().split("T")[0],
      end_date: new Date().toISOString().split("T")[0],
    },
  });

  const fetchReport = async (formData, userInitiated = false) => {
    try {
      setLoading(true);

      const startDate = new Date(formData.start_date);
      const syyyy = startDate.getFullYear();
      const smm = String(startDate.getMonth() + 1).padStart(2, "0");
      const sdd = String(startDate.getDate()).padStart(2, "0");

      const endDate = new Date(formData.end_date);
      const eyyyy = endDate.getFullYear();
      const emm = String(endDate.getMonth() + 1).padStart(2, "0");
      const edd = String(endDate.getDate()).padStart(2, "0");
      setFromDate(`${syyyy}-${smm}-${sdd}`);
      setToDate(`${eyyyy}-${emm}-${edd}`);

      const selectedCustomer = (formData.customer_ul_id || [])
        .map((item) => item?.value)
        .filter(Boolean);

      const payload = {
        customer_ul_id: selectedCustomer,
        start_date: `${syyyy}-${smm}-${sdd}`,
        end_date: `${eyyyy}-${emm}-${edd}`,
        report_type: REPORT_TYPE.LEDGER_REPORT,
      };

      const response = await post(LEDGER_REPORT, payload);
      setReport(response.data || []);
      setTotalPages(response.data.totalPages || 1);

      if (userInitiated) {
        setIsReportGenerated(true);
      }
    } catch (error) {
      console.error("Failed to fetch report:", error);
      toastr.error("Failed to generate report.");
    } finally {
      setLoading(false);
    }
  };

  const handleSendPdf = (item) => {
    setSelectedCustomer(item);
    setShowPdfModal(true);
  };

  const handleSendEmail = useCallback(
    async (customerData) => {
      try {
        const emailPayload = {
          pdf_data: {
            customer_name: customerData.customer_name,
            email: customerData.email,
            phone: customerData.phone,
            opening_balance: customerData.opening_balance || 0,
            total_qty: customerData.total_qty || 0,
            total_debit: customerData.total_debit || 0,
            total_credit: customerData.total_credit || 0,
            closing_balance: customerData.closing_balance || 0,
            orders: customerData.orders || {},
            fromDate: fromDate,
            toDate: toDate,
          },
        };

        const response = await post(SEND_PDF, emailPayload);
        toastr.success("Email sent successfully");
      } catch (error) {
        console.error("Failed to send email:", error);
        toastr.error("Failed to send email");
      }
    },
    [fromDate, toDate]
  ); // Add dependencies here if needed

  const columns = useMemo(
    () => ledgerColumns(handleSendPdf, handleSendEmail),
    [handleSendPdf, handleSendEmail]
  );

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs
            title="Custom Report"
            breadcrumbItems={breadcrumbItems}
          />

          <Card>
            <CardBody>
              <Form onSubmit={handleSubmit((data) => fetchReport(data, true))}>
                <Row className="align-items-end">
                  <Col md={2}>
                    <Label>Start Date</Label>
                    <Controller
                      name="start_date"
                      control={control}
                      render={({ field }) => (
                        <Flatpickr
                          {...field}
                          className="form-control"
                          options={{
                            dateFormat: "Y-m-d",
                            maxDate: "today",
                          }}
                        />
                      )}
                    />
                  </Col>
                  <Col md={2}>
                    <Label>End Date</Label>
                    <Controller
                      name="end_date"
                      control={control}
                      render={({ field }) => (
                        <Flatpickr
                          {...field}
                          className="form-control"
                          options={{
                            dateFormat: "Y-m-d",
                            maxDate: "today",
                          }}
                        />
                      )}
                    />
                  </Col>
                  <Col md={2}>
                    <Button color="primary" type="submit" className="w-100">
                      Generate Report
                    </Button>
                  </Col>
                </Row>
              </Form>
            </CardBody>
          </Card>

          <Card>
            <CardBody>
              {loading ? (
                <div className="text-center">Loading...</div>
              ) : isReportGenerated ? (
                report.length > 0 ? (
                  <TableContainer
                    columns={columns}
                    data={report}
                    isPagination={true}
                    customPageSize={pageSize}
                    iscustomPageSize={false}
                    isBordered={false}
                    controlledPagination={{
                      pageIndex,
                      setPageIndex,
                      pageSize,
                      setPageSize,
                      totalPages,
                    }}
                  />
                ) : (
                  <div className="text-center text-danger">
                    <h4 className="py-5">
                      No records found for the selected date.
                    </h4>
                  </div>
                )
              ) : (
                <div className="text-center text-muted">
                  <h4>Please select a date and generate the report.</h4>
                </div>
              )}
            </CardBody>
          </Card>
        </Container>
      </div>

      <Modal
        isOpen={showPdfModal}
        toggle={() => setShowPdfModal(false)}
        size="xl"
      >
        <ModalHeader toggle={() => setShowPdfModal(false)}>
          Preview PDF for {selectedCustomer?.customer_name}
        </ModalHeader>
        <ModalBody style={{ height: "80vh" }}>
          {selectedCustomer && (
            <PDFViewer width="100%" height="100%">
              <LedgerReportPdf data={[selectedCustomer]} />
            </PDFViewer>
          )}
        </ModalBody>
        <ModalFooter>
          {selectedCustomer && (
            <>
              <PDFDownloadLink
                document={<LedgerReportPdf data={[selectedCustomer]} />}
                fileName={`${selectedCustomer.customer_name}-report.pdf`}
                className="btn btn-success"
              >
                {({ loading }) => (loading ? "Preparing..." : "Download PDF")}
              </PDFDownloadLink>
              <Button
                color="primary"
                onClick={() => handleSendEmail(selectedCustomer)}
              >
                <i className="mdi mdi-email me-1"></i> Send Email
              </Button>
            </>
          )}
          <Button color="secondary" onClick={() => setShowPdfModal(false)}>
            Close
          </Button>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
};

export default LedgerReport;
